import { ScormError, SCORM_VERSION, SCORM_STATE, DataModelElement, IDataModelDictionary, IStringMap } from './index';

import 'es6-shim';

import 'whatwg-fetch';

const S_FALSE: string = 'false';
const S_TRUE: string = 'true';
const EMPTY_RESULT = '';
// TODO get this from somewhere else

/* Regular expressions used for validation */
const reCaseMatters = '(\\{case_matters=(true|false)\\})?';
const reCaseOrder =
    '((\\{case_matters=(true|false)\\}(\\{order_matters=(true|false)\\})?)|(\\{order_matters=(true|false)\\}(\\{case_matters=(true|false)\\})?))';
const reCommaDelimiter = '\\[,\\]';
const reCharacterString = '[\\u0000-\\uFFFF]';
const reLocalizedString = '((\\{lang=(\\w{1,3})(-\\S{2,8})?\\}))?([^\\{][\\u0000-\\uFFFF]{0,4000})?';
const reOrderMatters = '(\\{order_matters=(true|false)\\})?';
const rePerformanceStepAnswer = '((\\d{1,10}(\\.\\d+)?)\\[:\\](\\d{1,10}(\\.\\d+)?)|([\\u0000-\\uFFFF]{1,250}?))';
const reReal107 = '(\\d{1,10}(\\.\\d+)?)';
const reShortid = '[\\w\\.]{1,250}?';
const reNumeric = '(' + reReal107 + ')?\\[:\\](' + reReal107 + ')?';
const rePerformanceStep =
    '((' + reShortid + '\\[\\.\\](' + rePerformanceStepAnswer + ')?)|(' + reShortid + ')?\\[\\.\\]' + rePerformanceStepAnswer + ')';

/**
 * Implements the SCORM 1.2 (full) and 2004 (partial) API specifications
 */
export class SCORM_API {
    // for 1.2 & 2004 RTE required methods,
    // see  https://scorm.com/scorm-explained/technical-scorm/run-time/run-time-reference/

    // Items used in internal SCORM processing
    private allowLargeData = true; // Just allow this for now.
    private dataModel: IStringMap = {};
    private dataModelDict: IDataModelDictionary = {};
    private lastError: ScormError = new ScormError();
    private _scormVersion: SCORM_VERSION = SCORM_VERSION.SCORM_1_2;
    public state: SCORM_STATE = SCORM_STATE.NOT_INITIALIZED;
    private _terminateCallback: any = null;

    // Items used to make URLs, navigate or control windows
    public clearSysMsg: any = null;
    private controlMap: { [key: string]: string } = {};
    public doSysMsg: any = null;

    // Properties (vars with getters/setters)
    private _abe: string = null; // abe == API Back End
    private _debug = false;

    // Other properties used in processing
    private _commitTimerPid: number;
    private _traceModel = '';
    private _criticalModel: IStringMap = {};
    private _criticalKeys = [
        'cmi.core.score.raw',
        'cmi.core.lesson_status',
        'cmi.core.session_time',
        'cmi.score.raw',
        'cmi.completion_status',
        'cmi.suspend_data',
        'cmi.core.lesson_location',
    ];
    public _sessionStartTime: Date = undefined;
    private _bcmseq = 0;
    private _bcmidle = true;
    private _bcmwaiting = false;
    /* The payload of the request currently in the air, held so a failure can return it to
       the accumulator and a terminate can carry it as well. Null whenever nothing is in
       flight. */
    private _bcminflight: IStringMap = null;

    private lastPing: number;
    private lastEngagementPing: number;

    constructor() {
        this.log('SCORM 1.2 Interface has been loaded by default');
    }

    // --------------------------------------------------
    // SCORM 1.2 API Functions
    // --------------------------------------------------

    public LMSInitialize(param?: string) {
        this.log('LMSInitialize() was called.');
        let result = S_FALSE;
        this.resetLastError();
        if (param && param.length > 0) {
            this.setLastError(201, 201, 'Cannot pass a non-empty parameter to Initialize.  Received: "' + param + '"');
        } else if (this.state == SCORM_STATE.RUNNING) {
            this.setLastError(103, 101, 'Initialize was called previously');
        } else if (this.state == SCORM_STATE.TERMINATED) {
            this.setLastError(104, 101, 'Initialize failed because Terminate was already called');
        } else {
            this.state = SCORM_STATE.RUNNING;
            this.log('SCORM initialized');
            result = S_TRUE;
            this._sessionStartTime = new Date();
        }

        // send the critical map every x milliseconds
        /*
		if(result === S_TRUE) {
			this._commitTimerPid = setInterval(() => {
				this.beaconCriticalMap();
			}, 30000);
		}
		*/

        return result;
    }

    public LMSFinish(param?: any): string {
        this.log('LMSFinish() was called.');
        this.resetLastError();
        let result = S_FALSE;
        if (param && param.length > 0) {
            this.setLastError(201, 201, 'Cannot pass a non-empty parameter to Terminate.  Received: "' + param + '"');
        } else if (this.state == SCORM_STATE.NOT_INITIALIZED) {
            this.setLastError(112, 301, 'Cannot call Terminate before Initialize');
        } else if (this.state == SCORM_STATE.TERMINATED) {
            this.setLastError(113, 101, 'Already Terminated. Terminate was successfully called previously.');
        } else {
            result = S_TRUE;
        }
        this.log('Result ' + result + ' for LMSFinish ' + result);
        if (result === S_TRUE) {
            this.doTerminate();
        }
        return result;
    }

    public LMSGetValue(key: string): string {
        this.log('LMSGetValue() was called with key "' + key + '"');
        this.resetLastError();
        let result = EMPTY_RESULT;
        if (this.state == SCORM_STATE.NOT_INITIALIZED) {
            this.setLastError(122, 301, 'GetValue cannot be used before Initialize');
        } else if (this.state == SCORM_STATE.TERMINATED) {
            this.setLastError(123, 301, 'GetValue cannot be used after Terminate');
        }
        if (!key || key.length < 1) {
            this.setLastError(301, 201, 'Key is required for GetValue and cannot be empty');
        }

        if (this.lastError.noError()) {
            let element = this.dataModelForKey(key);
            if (element == null) {
                if (this.isChildren(key)) {
                    this.setLastError(301, 202, 'GetValue: Requested element "' + key + '" cannot have children');
                } else if (this.isCount(key)) {
                    this.setLastError(301, 203, 'GetValue: Requested element "' + key + '" is not an array, does not have count');
                } else if (this.startsWith(key, 'adl.data.')) {
                    this.setLastError(301, 201, 'GetValue: Invalid data model element "' + key + '"');
                } else {
                    // Element really not in the dictionary
                    this.setLastError(401, 201, 'GetValue: No such data model element "' + key + '"');
                }
            } else {
                if (element.writeOnly && element.writeOnly == true) {
                    this.setLastError(405, 404, 'Element ' + key + ' is write-only');
                } else {
                    if (element.array && element.array == true) {
                        result = this.getArrayValue(key, element);
                    } else {
                        result = this.getCommonValue(key, element);
                    }
                }
            }
        }
        if (result == null) result = ''; // just in case
        this.keepAlivePing();
        return result;
    }

    public LMSSetValue(key: string, value: string): string {
        this.log('LMSSetValue() was called with key "' + key + '" and value "' + value + '"');
        this.resetLastError();
        let result = S_FALSE;
        if (!key || key.length < 1) {
            this.setLastError(351, 351, 'Key is required for SetValue and cannot be empty');
        } else if (this.state == SCORM_STATE.NOT_INITIALIZED) {
            this.setLastError(132, 301, 'Cannot call SetValue before Initialize');
        } else if (this.state == SCORM_STATE.TERMINATED) {
            this.setLastError(133, 133, 'Cannot call SetValue after Terminate');
        } else {
            result = this.setValueImpl(key, value);
        }
        this.log('Result ' + result + ' for LMSSetValue ' + key + ' => ' + value);
        if (result == S_TRUE) {
            if (this._criticalKeys.indexOf(key) !== -1) {
                this._criticalModel[key] = value;
            }
        }
        this.keepAlivePing();
        return result;
    }

    public LMSCommit(param: string): string {
        this.log('LMSCommit() was called');
        let result = S_FALSE;
        this.resetLastError();
        if (param && param.length > 0) {
            this.setLastError(201, 201, 'Cannot pass a non-empty parameter to Commit');
        } else if (this.state == SCORM_STATE.NOT_INITIALIZED) {
            this.setLastError(142, 301, 'Cannot Commit before Initialize');
        } else if (this.state == SCORM_STATE.TERMINATED) {
            this.setLastError(143, 301, 'Cannot Commit after Terminate');
        } else {
            result = S_TRUE;
        }
        this.log('Result ' + result + ' for LMSCommit is ' + result);
        if (result === S_TRUE) {
            /* Commit is the SCO asking for its state to be saved now (DEV-8824). Until this
               the shell saved only when the window hid or the content terminated, so when
               that unload beacon was lost, so was the learner's progress. The coalescing in
               beaconCriticalMap keeps a chatty SCO to one request in flight, and a commit
               that changed nothing critical sends nothing. */
            this.beaconCriticalMap();
        }
        this.keepAlivePing();
        return result;
    }

    public LMSGetLastError(): string {
        this.log('LMSGetLastError called. Returning ' + this.lastError.getErrorCode(this._scormVersion));
        return this.lastError.getErrorCode(this._scormVersion);
    }

    public LMSGetErrorString(code: string): string {
        if (!code || code == '') {
            return '';
        }
        this.log(
            'LMSGetLastErrorString called with code ' + code + '. Returning ' + this.lastError.getErrorString(this._scormVersion, code)
        );
        return this.lastError.getErrorString(this._scormVersion, code);
    }

    public LMSGetDiagnostic(code: string): string {
        return this.lastError.getDiagnostic(this._scormVersion);
    }

    // --------------------------------------------------
    // SCORM 2004 API Functions
    // --------------------------------------------------

    public Initialize(param?: any): string {
        return this.LMSInitialize(param);
    }

    public GetValue(key: string): string {
        return this.LMSGetValue(key);
    }

    public SetValue(key: string, value: string): string {
        return this.LMSSetValue(key, value);
    }

    public Commit(emptyParam: string): string {
        return this.LMSCommit(emptyParam);
    }

    public GetLastError(): string {
        return this.LMSGetLastError();
    }

    public GetErrorString(code: string): string {
        return this.LMSGetErrorString(code);
    }

    public GetDiagnostic(code: string): string {
        return this.LMSGetDiagnostic(code);
    }

    public Terminate(param?: any): string {
        return this.LMSFinish(param);
    }

    // --------------------------------------------------
    // Utility Functions
    // --------------------------------------------------

    public sessionUnloading() {
        if (this._commitTimerPid && this._commitTimerPid > 0) {
            clearInterval(this._commitTimerPid);
            console.log('Commit Timer stopped');
            this._commitTimerPid = 0;
            this.beaconCriticalMap();
        }
    }

    /**
     * Sends the critical fields set since the last send.
     *
     * `unload` marks a send made as the page goes away. server-dc passes it on, so
     * learner-activity-service can tell a learner leaving from a learner working and won't
     * read a late delivery as fresh activity (DEV-8814).
     */
    private beaconCriticalMap(terminateAfter: boolean = false, unload: boolean = false) {
        let preview = false;
        const activityId = this.controlMap['control.activity.id'];
        const cmpreview = this.controlMap['control.element.preview'];
        const sessionId = this.controlMap['control.session.id'];
        console.log('beaconCriticalMap called, sessionId: ' + sessionId);

        this.trace('BCMS');

        if (cmpreview && cmpreview === 'true') {
            preview = true;
            this.log('Beacon critical map skipped because this is a preview');
            return;
        }
        if (this.abe && activityId && !preview) {
            if (Object.keys(this._criticalModel).length > 0) {
                const data = this.dataModel;

                data['ACTION'] = 'localstorage';
                data['SAID'] = activityId;
                data['SESSION_ID'] = sessionId;
                let now = new Date().getTime();
                data['TIMESTAMP'] = '' + now;
                this._criticalModel['SESSION_ID'] = sessionId;
                this._criticalModel['TIMESTAMP'] = '' + now;
                console.log('data to be sent to local storage: ', data);

                window.parent.postMessage(JSON.stringify(data), '*'); // to local storage

                var url = this.abe + '/scorm/commit/' + activityId;
                if (terminateAfter) {
                    url = this.abe + '/scorm/commitandterminate/' + activityId;
                }
                url += '?seq=' + this._bcmseq;
                if (unload || terminateAfter) {
                    url += '&unload=1';
                }
                if (this._criticalModel['cmi.core.lesson_status']) {
                    url += '&ccls=' + this._criticalModel['cmi.core.lesson_status'];
                }

                if (terminateAfter) {
                    this.log('Beacon critical map with terminate beginning');
                    const headers = {
                        type: 'application/json',
                    };
                    /* Carry anything still in flight as well. That request may not land
                       before the page goes away, and this beacon is the last chance the
                       learner's progress has of reaching the server. */
                    const finalModel = this.mergeBeneath(this._bcminflight, this._criticalModel);
                    finalModel['trace'] = this._traceModel;
                    const sendData = new Blob([JSON.stringify(finalModel)], headers);
                    if (navigator.sendBeacon(url, sendData)) {
                        this.trace('BCMOK');
                    } else {
                        this.trace('BCMF');
                    }
                } else if (this._bcmidle) {
                    this._bcmidle = false;
                    this._bcmwaiting = false;
                    this.log('Beacon critical map normal beginning');
                    const headers = {
                        type: 'application/json',
                    };
                    /* Take ownership of what is about to be sent and start a fresh
                       accumulator BEFORE the request leaves, because LMSSetValue keeps
                       writing into _criticalModel while it is in the air.

                       Clearing on success destroyed those newer values before they were
                       ever sent, and the queued retry then found an empty map and sent
                       nothing at all — the learner's progress died in the browser with no
                       error, no rejected write and no regression for the server to see.
                       A server-side guard cannot help with data that never arrives. */
                    const inflight = this._criticalModel;
                    this._criticalModel = {};
                    this._bcminflight = inflight;
                    inflight['trace'] = this._traceModel;
                    const sendData = new Blob([JSON.stringify(inflight)], headers);
                    /* keepalive only for a send that must outlive the page. Chrome gives a
                       page 64 KB in total for keepalive requests and beacons, and a SCO that
                       commits a few KB on every click used it up within minutes; from then
                       on every save, and the terminate beacon, failed before leaving the
                       browser. Reading the response releases the request either way. */
                    fetch(url, {
                        method: 'POST',
                        mode: 'cors',
                        keepalive: unload,
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: sendData,
                    })
                        .then((response) => {
                            response.text().catch(() => undefined);
                            /* Deliberately NOT clearing _criticalModel: whatever is in it
                               now arrived after this request was built and is still
                               unsent. This is the line that used to lose it. */
                            this._bcminflight = null;
                            if (this.clearSysMsg !== null) {
                                this.clearSysMsg();
                            }
                            this.log('Beacon critical completed successfully.');
                            this.trace('BCMOK');
                            this._bcmidle = true;
                            if (this._bcmwaiting) {
                                this.beaconCriticalMap();
                            }
                        })
                        .catch((error) => {
                            console.error('error in beaconCriticalMap fetch', error);
                            this.trace('BCMERR-' + error);
                            /* Nothing was acknowledged, so put the payload back — but
                               underneath anything newer, so a retry can never resurrect a
                               stale value over a fresher one. */
                            this._criticalModel = this.mergeBeneath(inflight, this._criticalModel);
                            this._bcminflight = null;
                            this._bcmidle = true;
                            /* Re-send only when the content asked for one. Re-sending
                               because the map is non-empty would spin on a persistent
                               network failure; the data now sitting in _criticalModel goes
                               out with the next commit either way. */
                            if (this._bcmwaiting) {
                                this.beaconCriticalMap();
                            }
                            //if(this2.doSysMsg !== null) {
                            //	this2.doSysMsg('There was a problem saving your data.  Some data may have been lost <a class="button" target="_blank" href="mailto:support@digitalchalk.com?subject=SCORM Debug info for ' + this.controlMap['control.activity.id'] + '&body=error ' + error + '  url ' + url + '  ' + JSON.stringify(this._criticalModel).replace(/\"/gi,"\'").replace(/&amp;/, "&") + '"><button>Email this problem to Support</button></a>');
                            //}
                        });
                } else if (unload) {
                    /* The page is going away with a commit still in the air. Waiting for it
                       means waiting on a completion callback that never runs once the page
                       is gone, so beacon now, carrying the in-flight payload beneath the
                       newer state, as terminate does. Nothing is cleared: the beacon is
                       unacknowledged, and if the page survives (a tab switch), the next
                       commit sends this state again. */
                    this.log('Beacon critical map on unload while a commit is in flight');
                    const sendData = new Blob([JSON.stringify(this.mergeBeneath(this._bcminflight, this._criticalModel))], {
                        type: 'application/json',
                    });
                    if (navigator.sendBeacon(url, sendData)) {
                        this.trace('BCMOK');
                    } else {
                        this.trace('BCMF');
                    }
                } else {
                    this.log('in else because of !_bcmidle');
                    this._bcmwaiting = true;
                }
            } else {
                this.log('Beacon critical map skipped. map is empty');
            }
        }
    }

    /**
     * `older` and `newer` combined, with `newer` winning every key it defines.
     *
     * Used to return an unacknowledged payload to the accumulator without undoing
     * anything the learner has done since — putting a stale snapshot back on top would
     * recreate, from the client side, exactly the regression this whole path exists to
     * avoid. Neither argument is mutated.
     */
    private mergeBeneath(older: IStringMap, newer: IStringMap): IStringMap {
        const result: IStringMap = {};
        if (older) {
            for (const key in older) {
                if (older.hasOwnProperty(key)) {
                    result[key] = older[key];
                }
            }
        }
        if (newer) {
            for (const key in newer) {
                if (newer.hasOwnProperty(key)) {
                    result[key] = newer[key];
                }
            }
        }
        return result;
    }

    private checkNoDuplicatesInList(value: string, delimiter: string): boolean {
        let result = true;
        if (value) {
            let items = value.split(new RegExp(delimiter));
            if (items.length > 1) {
                for (let i = 0; i < items.length - 1; i++) {
                    for (let k = i + 1; k < items.length; k++) {
                        if (items[i] == items[k]) {
                            result = false;
                            break; // out of the k loop
                        }
                    }
                    if (!result) {
                        break; // out of the i loop if false already found
                    }
                }
            }
        }
        return result;
    }

    private checkUnique(key: string, value: string, element: DataModelElement): boolean {
        let result = true;
        let pattern = new RegExp('^' + element.namePattern + '$');
        let m = pattern.exec(key);
        if (m && m.length > 2) {
            let firstIndex = m[3];
            pattern = new RegExp('^' + element.namePattern.replace('\\d+', firstIndex) + '$');
        }
        for (let prop in this.dataModel) {
            if (pattern.test(prop) && prop != key) {
                let propVal = this.dataModel[prop];
                if (propVal && propVal == value) {
                    result = false;
                    break;
                }
            }
        }
        return result;
    }

    /**
     * Saves as the content window's visibility changes. `leaving` is false when the window
     * becomes visible again: that save is the learner coming back, not going away.
     */
    public doPageHide(leaving: boolean = true) {
        this.trace('doPageHide');
        if (this._sessionStartTime) {
            let timespan = this.doubleToTimespan(Math.round((new Date().getTime() - this._sessionStartTime.getTime()) / 1000));
            this._criticalModel['cmi.core.session_time'] = timespan;
        }
        this.beaconCriticalMap(false, leaving);
    }

    private doTerminate() {
        if (this._sessionStartTime) {
            let timespan = this.doubleToTimespan(Math.round((new Date().getTime() - this._sessionStartTime.getTime()) / 1000));
            this._criticalModel['cmi.core.session_time'] = timespan;
        }
        this.beaconCriticalMap(true);
        if (this._terminateCallback) {
            setTimeout(() => {
                this._terminateCallback();
            }, 500);
        }
    }

    private dataModelForKey(key: string): DataModelElement {
        var result = null;
        if (key in this.dataModelDict) {
            result = this.dataModelDict[key];
        } else {
            let regex: RegExp = null;
            for (let elementName in this.dataModelDict) {
                if (this.dataModelDict[elementName].namePattern) {
                    regex = new RegExp('^' + this.dataModelDict[elementName].namePattern + '$');
                    if (regex.test(key)) {
                        result = this.dataModelDict[elementName];
                        break;
                    }
                }
            }
        }

        return result;
    }

    // needed, because endsWith exists only in ES6, which isn't supported by.. I don't really have to tell you which browser do I?  (Hint: It's IE 11)
    private endsWith(haystack: string, needle: string): boolean {
        return haystack.indexOf(needle, haystack.length - needle.length) !== -1;
    }

    public finished() {
        this.log('Package is finished');
        this.doTerminate();
    }

    private getArrayValue(key: string, element: DataModelElement): string {
        let result = '';
        let indexRegex = new RegExp(element.namePattern);
        let m = indexRegex.exec(key);
        if (m && m.length > 2) {
            if (m.length > 4) {
                // array of arrays, e.g. cmi.objective.1.response.2
                let elecount1 = parseInt(this.getElementCount(m[2] + '._count'));
                let thisIndex = parseInt(m[3]);
                if (thisIndex < elecount1) {
                    let elecount2 = parseInt(this.getElementCount(m[1]));
                    let index2 = parseInt(m[4]);
                    if (index2 < elecount2) {
                        result = this.getCommonValue(key, element);
                    } else if (index2 == elecount2 && this.endsWith(key, '_count')) {
                        result = this.getCommonValue(key, element);
                    } else {
                        this.setLastError(301, 201, 'Element index is out of range: ' + key);
                    }
                } else {
                    this.setLastError(301, 201, 'Element index is out of range: ' + key);
                }
            } else {
                // simple array, e.g. cmi.objective.1
                let elecount = parseInt(this.getElementCount(m[1] + '._count'));
                let thisIndex = parseInt(m[2]);
                if (thisIndex < elecount) {
                    result = this.getCommonValue(key, element);
                } else if (thisIndex === elecount && this.endsWith(key, '_count')) {
                    result = this.getCommonValue(key, element);
                } else {
                    this.setLastError(301, 201, 'Element index is out of range: ' + key);
                }
            }
        } else {
            // This shouldn't happen, but just in case
            this.setLastError(401, 201, 'Invalid data model element: ' + key);
        }
        return result;
    }

    private getCommonValue(key: string, element: DataModelElement): string {
        let result = '';
        if (element.writeOnly && element.writeOnly == true) {
            this.setLastError(405, 404, 'Element ' + key + ' is write-only');
        } else if (this.endsWith(key, '._count')) {
            return this.getElementCount(key);
        } else {
            if (key in this.dataModel) {
                result = this.dataModel[key];
            } else if (element.defaultValue) {
                result = element.defaultValue;
            } else if (this._scormVersion == SCORM_VERSION.SCORM_2004 && element.setBeforeGet && element.setBeforeGet == true) {
                this.setLastError(403, 0, 'Data Model Element Not Initialized');
            }
        }
        return result;
    }

    private getElementCount(key: string): string {
        let result = 0;
        var regex = new RegExp('(.*?)\\._count');
        var matches = regex.exec(key);
        if (matches && matches.length > 1) {
            let prefix = matches[1];
            let pregex = new RegExp(prefix + '.(\\d+).*');
            for (let modelKey in this.dataModel) {
                let pmat = pregex.exec(modelKey);
                if (pmat && pmat.length > 1) {
                    let numkey = parseInt(pmat[1]);
                    if (numkey >= result) {
                        result = numkey + 1; // zero-based indexing, so foo.0 == 1 count, foo.0,foo.1 == 2 count, etc
                    }
                }
            }
        }

        return result.toString();
    }

    private getReferenceElementValue(key: string, element: DataModelElement): string {
        let result = null;
        let refPattern = element.referenceElement;
        if (refPattern) {
            let re = new RegExp('^' + element.namePattern + '$');
            let m = re.exec(key);
            if (m && m.length > 0) {
                for (let i = 0; i < m.length; i++) {
                    refPattern = refPattern.replace('$' + i, m[i]);
                }
                if (refPattern in this.dataModel) {
                    result = this.dataModel[refPattern];
                }
            }
        }
        return result;
    }

    private isChildren(key: string): boolean {
        return this.endsWith(key, '._children');
    }

    private isCount(key: string): boolean {
        return this.endsWith(key, '._count');
    }

    public loadDataModelElements(modelName?: string, callback?: () => void) {
        this._scormVersion = SCORM_VERSION.SCORM_1_2;
        if (!modelName) {
            if ('control.scorm.2004' in this.controlMap) {
                this._scormVersion = SCORM_VERSION.SCORM_2004;
                modelName = 'dataModel2004.json';
            } else {
                modelName = 'dataModel12.json';
            }
        }
        let request = new XMLHttpRequest();
        request.open('get', 'assets/' + modelName, true);
        request.withCredentials = true;
        /* The callback opens the content window (singleapi.html), so it must
           run AT MOST ONCE PER CALL. onreadystatechange is an event, not a
           promise: anything that delivers readyState 4 a second time runs the
           whole body again, and a second window.open() cannot be absorbed — an
           unnamed window.open always creates a new window. That is DEV-7607:
           two singleframed.html windows 156 ms apart from a single launch, one
           of which never receives its content URL and stays blank.

           Scope is deliberately this invocation, not the instance: modelName
           is a parameter, so a caller may legitimately load a second model.
           Two separate CALLS still mean two callbacks — the caller owns that,
           which is why singleapi.html guards launch() and the window open
           independently rather than relying on this flag. */
        let delivered = false;
        request.onreadystatechange = () => {
            if (request.readyState == 4 && request.status == 200) {
                if (delivered) {
                    /* trace(), not log(): log is gated on debug, which is false
                       in production, and the trace string is how DEV-7607 was
                       diagnosed in the first place. A recurrence in the field
                       has to leave a mark. */
                    this.trace('dataModelLoad/repeat ignored');
                    return;
                }
                delivered = true;
                let dataList = JSON.parse(request.responseText);
                for (var d of dataList) {
                    this.dataModelDict[(<DataModelElement>d).namePattern] = d;
                }
                this.log('Data model dictionary has been loaded');
                if (callback) {
                    callback();
                }
            } else if (request.readyState == 4) {
                /* The data model failed to load, so the callback never runs and
                   no content window is ever opened — the learner sits on an
                   empty singleapi.html. Recovering from this needs a failure
                   channel the callback signature does not have, so this only
                   makes it diagnosable; the recovery path is its own change. */
                this.trace('dataModelLoad/failed-' + request.status);
            }
        };
        request.send();
    }

    private resetLastError() {
        this.lastError = new ScormError(); // == 0, No Error
    }

    private setArrayValue(key: string, value: string, element: DataModelElement): string {
        let result = S_FALSE;

        let indexRegex = new RegExp(element.namePattern);
        let m = indexRegex.exec(key);
        if (m && m.length > 2) {
            if (m.length > 4) {
                // double array match
                // m0 = full match, m1 = root2, m2 = root1, m3 = index1, m4 = index2
                let elecount1 = parseInt(this.getElementCount(m[2] + '._count'));
                let thisIndex = parseInt(m[3]);
                if (thisIndex <= elecount1) {
                    let elecount2 = parseInt(this.getElementCount(m[1] + '._count'));
                    let index2 = parseInt(m[4]);
                    if (index2 <= elecount2) {
                        if (element.prerequisite) {
                            // SCORM 2004 only
                            if (this.verifyPrerequisite(element.prerequisite, m)) {
                                result = this.setCommonValue(key, value, element);
                            } else {
                                this.setLastError(408, 201, 'Data Model Element dependency not established for ' + key);
                            }
                        } else {
                            result = this.setCommonValue(key, value, element);
                        }
                    } else {
                        this.setLastError(351, 201, 'Element index is out of range for set on ' + key);
                    }
                } else {
                    this.setLastError(351, 201, 'Element index is out of range for set on ' + key);
                }
            } else {
                // simple array match
                // m[0] is full match, m[1] is root, m[2] is the index
                let elecount = parseInt(this.getElementCount(m[1] + '._count'));
                let thisIndex = parseInt(m[2]);
                if (thisIndex <= elecount) {
                    if (element.prerequisite) {
                        if (this.verifyPrerequisite(element.prerequisite, m)) {
                            result = this.setCommonValue(key, value, element);
                        } else {
                            this.setLastError(408, 201, 'Data Model Element dependency not established for ' + key);
                        }
                    } else {
                        result = this.setCommonValue(key, value, element);
                    }
                } else {
                    this.setLastError(351, 201, 'Element index is out of range for set on ' + key);
                }
            }
        } else {
            // something went haywire with the regex...
            this.setLastError(401, 401, 'Invalid data model element ' + key);
        }

        return result;
    }

    private setCommonValue(key: string, value: string, element: DataModelElement): string {
        let result = S_FALSE;
        if (element.dataType && !this.validateType(key, value, element)) {
            if (this.lastError.noError()) {
                this.setLastError(406, 405, 'Incorrect data type for ' + key);
            }
        } else if ((element.min || element.max) && !this.validateNumberMinMax(key, value, element)) {
            this.setLastError(407, 405, 'Numeric value out of range for data element ' + key);
        } else {
            result = S_TRUE;
        }
        return result;
    }

    private setCompletionStatus(value: string) {
        let threshold = 'cmi.completion.threshold' in this.dataModel ? this.dataModel['cmi.completion.threshold'] : null;
        let progress = 'cmi.progress_measure' in this.dataModel ? this.dataModel['cmi.progress_measure'] : null;
        if (threshold) {
            if (!progress) {
                // e.g. we need 50% to complete, but we don't know what % we've done
                this.dataModel['cmi.completion_status'] = 'unknown';
            } else if (parseInt(progress) < parseInt(threshold)) {
                // e.g. need 50, got 20
                this.dataModel['cmi.completion_status'] = 'incomplete';
            } else {
                // e.g. need 50, got 70
                this.dataModel['cmi.completion_status'] = 'completed';
            }
        } else if (progress) {
            // e.g we got 50% done, but how much is finished?
            this.dataModel['cmi.completion_status'] = 'unknown';
        } else {
            this.dataModel['cmi.completion_status'] = value;
        }
    }

    public setControlMap(newMap: any) {
        for (var key in newMap) {
            if (this.startsWith(key, 'control')) {
                this.controlMap[key] = newMap[key];
            } else if (this.startsWith(key, 'trace')) {
                this._traceModel = newMap[key];
            } else {
                if (key != 'cmi.core.session_time') {
                    // don't carry over session time, each session gets one
                    this.dataModel[key] = newMap[key];
                }
            }
        }
    }

    private setLastError(code2004: number, code12: number, diagnostic?: string) {
        this.lastError = new ScormError(code2004, code12, diagnostic);
    }

    private setValueImpl(key: string, value: string): string {
        let result = S_FALSE;
        let element = this.dataModelForKey(key);
        if (!element) {
            if (this.startsWith(key, 'adl.data.')) {
                this.setLastError(351, 201, 'Invalid data model element on set ' + key);
            } else {
                this.setLastError(401, 201, 'Invalid data model element on set ' + key);
            }
        } else {
            if (this.isChildren(key) || this.isCount(key)) {
                this.setLastError(404, 402, 'Element ' + key + ' cannot be set since it is a reserved word');
            } else if (element.readOnly) {
                this.setLastError(404, 403, 'Element ' + key + ' is read-only');
            } else if (element.writeOnce && key in this.dataModel) {
                this.setLastError(351, 201, 'Element ' + key + ' can only be set once, and it has been previously set');
            } else if (element.uniqueInArray && !this.checkUnique(key, value, element)) {
                this.setLastError(351, 201, 'Element ' + key + ' must be unique value in the array');
            } else if (element.array) {
                result = this.setArrayValue(key, value, element);
            } else {
                result = this.setCommonValue(key, value, element);
            }
        }
        if (result == S_TRUE) {
            // special cases first
            if (key == 'cmi.comments') {
                if (key in this.dataModel && this.dataModel[key] != null) {
                    this.dataModel[key] = this.dataModel[key] + value.toString();
                } else {
                    this.dataModel[key] = value.toString();
                }
            } else if (key.match(/adl.data.\d+.store/) && value == '') {
                if (key in this.dataModel) {
                    delete this.dataModel[key];
                }
            } else if (key == 'cmi.completion_status') {
                this.setCompletionStatus(value);
            } else {
                this.dataModel[key] = value.toString();
            }

            // POST
            if (key == 'cmi.progress_measure') {
                this.setCompletionStatus(null);
            }
        }
        return result;
    }

    // see comment on endsWith. No IE 11 support for String.startsWith ES6
    private startsWith(haystack: string, needle: string): boolean {
        return haystack.substr(0, needle.length) == needle;
    }

    private validateType(key: string, value: string, element: DataModelElement): boolean {
        let result = false;

        // Allowed empty values don't need validation
        if (!element.notEmpty) {
            if (value && value == '') {
                result = true;
            }
        }
        if (!result) {
            if (!element.dataType) {
                result = true; // no validation on untyped fields
            } else if (element.dataType == 'language') {
                result = this.validateTypeLanguage(value);
            } else if (element.dataType == 'longid') {
                result = this.validateTypeLongid(value);
            } else if (element.dataType == 'integer') {
                result = this.validateTypeInteger(value);
            } else if (element.dataType == 'feedback') {
                result = this.validateTypeFeedback(key, value, element); // e.g. learner_response
            } else if (element.dataType == 'localizedstring') {
                result = this.validateTypeLocalizedString(value);
            } else if (element.dataType == 'pattern') {
                result = this.validateTypePattern(key, value, element); // e.g. correct_responses
            } else if (element.dataType == 'real') {
                result = this.validateTypeReal(value);
            } else if (element.dataType == 'time') {
                result = this.validateTypeTime(value);
            } else if (element.dataType == 'timeinterval') {
                result = this.validateTypeTimeInterval(value);
            } else if (element.dataType == 'CMIBlank') {
                result = this.validateTypeCMIBlank(value);
            } else if (element.dataType == 'CMIBoolean') {
                result = this.validateTypeCMIBoolean(value);
            } else if (element.dataType == 'CMIDecimal') {
                result = this.validateTypeCMIDecimal(value, element);
            } else if (element.dataType == 'CMIFeedback') {
                result = this.validateTypeCMIFeedback(key, value);
            } else if (element.dataType == 'CMIIdentifier') {
                result = this.validateTypeCMIid(value, element);
            } else if (element.dataType == 'CMISInteger') {
                result = this.validateTypeCMISInteger(value, element);
            } else if (element.dataType == 'CMIString255') {
                result = this.validateTypeCMIString255(value);
            } else if (element.dataType == 'CMIString4096') {
                result = this.validateTypeCMIString4096(value);
            } else if (element.dataType == 'CMIString64k') {
                result = this.validateTypeCMIString64k(value);
            } else if (element.dataType == 'CMITime') {
                result = this.validateTypeCMITime(value);
            } else if (element.dataType == 'CMITimespan') {
                result = this.validateTypeCMITimespan(value);
            } else if (element.dataType == 'CMIVocabulary' || element.dataType == 'state') {
                result = this.validateTypeCMIVocabulary(key, value, element);
            } else {
                result = true;
            }
        }

        return result;
    }

    private validateTypePattern(key: string, value: string, element: DataModelElement): boolean {
        let result = true;
        let refValue = this.getReferenceElementValue(key, element);
        if (refValue) {
            let re = null;
            if (refValue == 'true-false') {
                if (this.validateMaxIndex(key, element, 0)) {
                    re = new RegExp('^((true)|(false))$');
                    result = re.test(value);
                } else {
                    this.setLastError(351, 101, 'Only one pattern is allowed for true-false types');
                    result = false;
                }
            } else if (refValue == 'choice') {
                // Empty OK here.. see RTE 4.2.9.1
                if (this.checkNoDuplicatesInList(value, reCommaDelimiter)) {
                    if (this.checkUnique(key, value, element)) {
                        re = new RegExp('^' + reShortid + '(\\[,\\]' + reShortid + '){0,9}$');
                        result = re.test(value);
                    } else {
                        this.setLastError(351, 101, 'Duplicate choice pattern is not allowed');
                        result = false;
                    }
                } else {
                    // no dups allowed inside the value, e.g. 1[,]1
                    this.setLastError(406, 101, 'Duplicate items are not allowed inside a choice list');
                    result = false;
                }
            } else if (refValue == 'fill-in') {
                re = new RegExp('^' + reCaseOrder + reLocalizedString + '(\\[,\\] ' + reLocalizedString + '){0,4}$');
                result = re.test(value);
            } else if (refValue == 'long-fill-in') {
                re = new RegExp('^' + reCaseMatters + reLocalizedString + '$');
                result = re.test(value);
            } else if (refValue == 'likert') {
                // likert == Strongly Agree, Agree, Disagree, etc. type scale. labels might be customized
                if (this.validateMaxIndex(key, element, 0)) {
                    re = new RegExp('^\\S+$');
                    result = re.test(value);
                } else {
                    this.setLastError(351, 101, 'Only one pattern is allowed for likert type interactions');
                    result = false;
                }
            } else if (refValue == 'matching') {
                re = new RegExp('^([^\\[\\]\\s])+\\[\\.\\]([^\\[\\]\\s])+(\\[,\\]([^\\[\\]\\s])+\\[\\.\\]([^\\[\\]\\s])+){0.35}$');
                result = re.test(value);
            } else if (refValue == 'performance') {
                // No docs here except it's existence, just let it pass like the old scorm api did
            } else if (refValue == 'sequencing') {
                re = new RegExp('^' + reShortid + '(\\[,\\]' + reShortid + '){0,35}$');
                result = re.test(value);
            } else if (refValue == 'numeric') {
                if (this.validateMaxIndex(key, element, 0)) {
                    let steps = value.split(/\\[,\\]/); // 1[,]2[,]3
                    for (let step in steps) {
                        if (!steps[step].match('^' + reNumeric + '$')) {
                            result = false; // one of the steps isn't a valid number
                        }
                    }
                } else {
                    this.setLastError(351, 101, 'Only one pattern is allowed for numeric interaction types');
                    result = false;
                }
            } else if (refValue == 'other') {
                if (this.validateMaxIndex(key, element, 0)) {
                    re = new RegExp('^' + reCharacterString + '{0,4000}$');
                    result = re.test(value);
                } else {
                    this.setLastError(351, 101, 'Only one pattern is allowed for for "other" interaction types');
                    result = false;
                }
            }
        }
        return result;
    }

    private validateTypeFeedback(key: string, value: string, element: DataModelElement): boolean {
        let result = true;
        let refValue = this.getReferenceElementValue(key, element);
        if (refValue) {
            let re = null;
            if (refValue == 'true-false') {
                re = new RegExp('^((true)|(false))$');
                result = re.test(value);
            } else if (refValue == 'choice') {
                // Empty OK for response, but not pattern
                re = new RegExp('^(' + reShortid + '(\\[,\\]' + reShortid + '){0,9})?$');
                result = re.test(value);
            } else if (refValue == 'fill-in') {
                re = new RegExp('^' + reLocalizedString + '(\\[,\\]' + reLocalizedString + '){0,8}$');
                result = re.test(value);
            } else if (refValue == 'long-fill-in') {
                re = new RegExp('^' + reLocalizedString + '$');
                result = re.test(value);
            } else if (refValue == 'likert') {
                re = new RegExp('^' + reShortid + '$');
                result = re.test(value);
            } else if (refValue == 'matching') {
                re = new RegExp(
                    '^(' + reShortid + '\\[\\.\\]' + reShortid + '(\\[,\\]' + reShortid + '\\[\\.\\]' + reShortid + '){0,35})?$'
                );
                result = re.test(value);
            } else if (refValue == 'performance') {
                // unimplemented in old api
            } else if (refValue == 'sequencing') {
                re = new RegExp('^(' + reShortid + '(\\[,\\]' + reShortid + '){0,34})?$');
                result = re.test(value);
            } else if (refValue == 'numeric') {
                re = new RegExp('^' + reReal107 + '$');
                result = re.test(value);
            } else if (refValue == 'other') {
                re = new RegExp('^' + reCharacterString + '{0,4000}$');
                result = re.test(value);
            } else {
                result = false;
            }
        } else {
            result = false;
        }
        return result;
    }

    private validateTypeCMIString255(value: string): boolean {
        let re = new RegExp('^[\\x00-\\x7F]{0,255}$');
        return re.test(value);
    }

    private validateTypeCMIString4096(value: string): boolean {
        let re = new RegExp('^[\\x00-\\x7F]{0,4096}$');
        return re.test(value);
    }

    private validateTypeCMIString64k(value: string): boolean {
        if (!this.allowLargeData) {
            return this.validateTypeCMIString4096(value);
        } else {
            let re = new RegExp('^[\\x00-\\x7F]{0,65536}$');
            return re.test(value);
        }
    }

    private validateTypeCMIInteger(value: string, element: DataModelElement): boolean {
        // seems to be unused, keeping for future reference
        // CMIInteger uses validateTypeCMISInteger (short int)
        let result = false;
        let re = new RegExp('^\\d+$');
        if (re.test(value)) {
            let intValue = parseInt(value);
            if (intValue >= 0 && intValue <= 65536) {
                result = true;
            }
        }
        return result;
    }

    private validateTypeCMISInteger(value: string, element: DataModelElement): boolean {
        // SInteger == short integer
        let result = false;
        let re = new RegExp('^\\-?\\d+$');
        if (re.test(value)) {
            let intValue = parseInt(value);
            if (intValue >= -32768 && intValue <= 32768) {
                result = true;
            }
        }
        return result;
    }

    private validateTypeCMIid(value: string, element: DataModelElement): boolean {
        let re = new RegExp('^[\\w\\.\\-_~]{1,255}$');
        return re.test(value);
    }

    private validateTypeCMIFeedback(key: string, value: string): boolean {
        // old scorm api did this, never seemed to be a problem?
        return true;
    }

    private validateTypeCMIDecimal(value: string, element: DataModelElement) {
        var re = new RegExp('^\\-?\\d+(\\.\\d+)?$');
        return re.test(value);
    }

    private validateTypeCMIBoolean(value: string): boolean {
        let result = false;
        if (value) {
            if (value == 'true' || value == 'false') {
                result = true; // true as in this is a valid boolean, not the actual value (which of course is true or false)
            }
        }
        return result;
    }

    private validateTypeCMIBlank(value: string): boolean {
        return value == '';
    }

    private validateTypeTimeInterval(value: string): boolean {
        if (!value) {
            return false;
        } else if (this.endsWith(value, 'T')) {
            // Cant indicate Time (T) without H,M, or S
            return false;
        } else {
            let re = new RegExp('^P((\\d+)Y)?((\\d+)M)?((\\d+)D)?(T((\\d+)H)?((\\d+)M)?((\\d+(\\.\\d{1,2})?)S)?)?$');
            return re.test(value);
        }
    }

    private validateTypeReal(value: string): boolean {
        let re = new RegExp('^([+-])?(\\d{1,10})(\\.\\d+)?$'); // See ADL RTE comment in sec 4.1.1.7
        return re.test(value);
    }

    private validateTypeLanguage(value: string): boolean {
        let re = new RegExp('^\\w{1,3}(-[\\w-]{2,8})?$');
        return re.test(value);
    }

    private validateTypeLongid(value: string): boolean {
        let pattern = new RegExp('^\\S+$');
        if (typeof value == 'undefined' || value == null) {
            return false;
        }
        if (this.startsWith(value, 'urn:')) {
            // if you are going to use an URN, you have to do it correctly
            pattern = new RegExp('^urn:\\S+:\\S+$');
        }
        return pattern.test(value);
    }

    private validateTypeInteger(value: string): boolean {
        let re = new RegExp('^\\d+$');
        return re.test(value);
    }

    private validateTypeLocalizedString(value: string): boolean {
        var re = new RegExp('^' + reLocalizedString + '$');
        return re.test(value);
    }

    private validateNumberMinMax(key: string, value: string, element: DataModelElement): boolean {
        let result = true;

        if (isNaN(parseInt(value))) {
            result = false;
        } else {
            let v = parseInt(value);
            if (element.min != null) {
                if (v < element.min) {
                    result = false;
                }
            }
            if (element.max != null) {
                if (v > element.max) {
                    result = false;
                }
            }
        }

        return result;
    }

    private validateTypeTime(value: string): boolean {
        let result = true;
        let re = new RegExp(
            '^(\\d{4})(-(\\d\\d)(-(\\d\\d)(T(\\d\\d)(:(\\d\\d)(:(\\d\\d)(\\.(\\d{1,2})(Z|([\\+\\-](\\d\\d)(:(\\d\\d))?))?)?)?)?)?)?)?$'
        );
        let m = re.exec(value);
        if (m) {
            try {
                if (m[1]) {
                    // YYYY 1970-2038
                    let year = parseInt(m[1]);
                    if (isNaN(year) || year < 1970 || year > 2038) {
                        result = false;
                    }
                }
                if (result && m[3]) {
                    // MM 01-12
                    let month = parseInt(m[3]);
                    if (isNaN(month) || month < 1 || month > 12) {
                        result = false;
                    }
                }
                if (result && m[5]) {
                    // DD 01-31, depending on month
                    let day = parseInt(m[5]);
                    let month = parseInt(m[3]);
                    let year = parseInt(m[1]);
                    if (isNaN(day) || isNaN(month) || isNaN(year) || day < 1 || day > 31) {
                        result = false;
                    }
                    if (result) {
                        if (month == 4 || month == 6 || month == 9 || month == 11) {
                            // Thirty days hath September, April, June, and November
                            if (day > 30) {
                                result = false;
                            }
                        } else if (month == 2) {
                            // Stupid february :)
                            if (year % 4 == 0) {
                                // a leap year.  don't need the other leap rules, scorm spec dictates 1970-2038
                                if (day > 29) {
                                    result = false;
                                }
                            } else {
                                if (day > 28) {
                                    result = false;
                                }
                            }
                        }
                    }
                }
                if (result && m[7]) {
                    // HH 00-23
                    let hours = parseInt(m[7]);
                    if (isNaN(hours) || hours < 0 || hours > 23) {
                        result = false;
                    }
                }
                if (result && m[9]) {
                    // MM 00-59
                    let mins = parseInt(m[9]);
                    if (isNaN(mins) || mins < 0 || mins > 59) {
                        result = false;
                    }
                }
                if (result && m[11]) {
                    // Secs 00-59
                    let secs = parseInt(m[11]);
                    if (isNaN(secs) || secs < 0 || secs > 59) {
                        result = false;
                    }
                }
                if (result && m[13]) {
                    // "fractional" secs 0-99
                    let fsecs = parseInt(m[13]);
                    if (isNaN(fsecs) || fsecs < 0 || fsecs > 99) {
                        result = false;
                    }
                }
                if (result && m[16]) {
                    // HH in timezone (e.g. +05:00)
                    let htz = parseInt(m[16]);
                    if (isNaN(htz) || htz < 0 || htz > 23) {
                        result = false;
                    }
                }
                if (result && m[18]) {
                    // MM in timezone
                    let mtz = parseInt(m[18]);
                    if (isNaN(mtz) || mtz < 0 || mtz > 59) {
                        result = false;
                    }
                }
            } catch (exc) {
                // probably a bad number
                result = false;
            }
        } else {
            result = false;
        }
        return result;
    }

    private validateTypeCMIVocabulary(key: string, value: string, element: DataModelElement): boolean {
        let result = false;
        if (element.validValues) {
            for (let validValue of element.validValues) {
                if (value == validValue) {
                    // string literal
                    result = true;
                    break;
                }
                if (this.startsWith(validValue, '^')) {
                    // found a regex
                    let re = new RegExp(validValue);
                    if (re.test(value)) {
                        result = true;
                        break;
                    }
                }
            }
        }
        return result;
    }

    private validateTypeCMITimespan(value: string): boolean {
        let result = false;
        let re = new RegExp('^(\\d{2,4}):(\\d\\d):(\\d\\d(\\.\\d{1,2})?)$');
        let m = re.exec(value);
        if (m) {
            try {
                //m1 == hours, m2 == mins, m3==secs, m4 (not used), m5 == millis (optional)
                //unlike CMITime, no sanity check on timespan (for example, minutes can be > 60)
                if (m.length > 3) {
                    let hours = parseInt(m[1]);
                    let mins = parseInt(m[2]);
                    let secs = parseInt(m[3]);
                    if (hours >= 0 && mins >= 0 && secs >= 0) {
                        result = true;
                    }
                }
            } catch (exc) {
                result = false;
            }
        }
        return result;
    }

    private validateTypeCMITime(value: string): boolean {
        let result = false;
        let re = new RegExp('^(\\d{2}):(\\d\\d):(\\d\\d(\\.\\d{1,2})?)$');
        let m = re.exec(value);
        try {
            if (m) {
                if (m.length > 3) {
                    let hours = parseInt(m[1]);
                    let mins = parseInt(m[2]);
                    let secs = parseInt(m[3]);
                    if (hours >= 0 && hours < 24) {
                        if (mins >= 0 && mins < 60) {
                            if (secs >= 0 && secs < 60) {
                                result = true;
                            }
                        }
                    }
                }
            }
        } catch (exc) {
            result = false;
        }
        return result;
    }

    private validateMaxIndex(key: string, element: DataModelElement, maxIndex: number): boolean {
        let result = true;
        let reName = new RegExp('^' + element.namePattern + '$');
        let m = reName.exec(key);
        if (m) {
            let thisIndex = 0;
            if (m.length > 2) {
                thisIndex = parseInt(m[4]);
            } else {
                thisIndex = parseInt(m[2]);
            }
            result = thisIndex < maxIndex;
        }
        return result;
    }

    private verifyPrerequisite(key: string, m: RegExpExecArray): boolean {
        if (m && m.length > 0) {
            for (let i = 0; i < m.length; i++) {
                if (m[i]) {
                    key = key.replace('$' + i, m[i]);
                }
            }
        }
        if (key in this.dataModel && this.dataModel[key] != null) {
            return true;
        } else {
            return false;
        }
    }

    private pad(n: string, width: number, z?: string): string {
        z = z || '0';
        n = n + '';
        return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
    }

    private doubleToTimespan(secs: number): string {
        let result: string = '';
        let rsecs: number = Math.floor(secs);
        let iHours: number = Math.floor(rsecs / 3600);
        rsecs -= iHours * 3600;

        let iMins = Math.floor(rsecs / 60);
        rsecs -= iMins * 60;

        let msecs = (secs - Math.floor(secs)) * 100;
        // sample: 0000:00:00.00
        // sample: 03:01:39.52
        // Why, oh why, doesn't javascript have String.format or sprintf???
        if (iHours >= 100) {
            result += this.pad(iHours.toString(), 4);
        } else {
            result += this.pad(iHours.toString(), 2);
        }
        result += ':';
        result += this.pad(iMins.toString(), 2);
        result += ':';
        result += this.pad(rsecs.toFixed(0).toString(), 2);
        if (msecs > 0) {
            result += '.' + this.pad(msecs.toFixed(0).toString(), 2);
        }
        return result;
    }

    public keepAlivePing() {
        let now = Date.now();
        if (!this.lastPing || now - this.lastPing > 1000 * 60) {
            console.log('keepAlivePing');

            window.parent.postMessage('{"ACTION":"keepalive"}', '*');

            // const url = this.abe + "/user/auth-check";
            // let request = new XMLHttpRequest();
            // request.open("get", url, true);
            // request.withCredentials = true;
            // request.send();
            this.lastPing = now;
        }
    }

    /* Real input in the content window, relayed by the engagement detector. The deliver
       page counted every keepalive as input, so a package that commits on a timer held an
       absent learner ONLINE indefinitely (DEV-7937). "engagement" is posted only from here;
       the keepalive alongside it is for a deliver page that predates the new action, which
       must still hear from an engaged learner. Its own throttle, so API traffic that has
       just spent keepalive's minute can't swallow a learner's input. */
    public engagementPing() {
        let now = Date.now();
        if (!this.lastEngagementPing || now - this.lastEngagementPing > 1000 * 60) {
            window.parent.postMessage('{"ACTION":"engagement"}', '*');
            this.lastEngagementPing = now;
        }
        this.keepAlivePing();
    }

    /* The engagement detector found a frame it can't read (cross-origin), so input
       inside it reaches no one. The deliver page goes back to counting this package's
       keepalive as presence for the rest of the session (DEV-7937): a learner working
       in that frame keeps the old, over-generous credit rather than being clamped. */
    public unreadableFramePing() {
        window.parent.postMessage('{"ACTION":"unreadableFrame"}', '*');
    }

    private log(message: any) {
        if (this.debug && typeof console != 'undefined') {
            console.log(message);
        }
    }

    public trace(message: any) {
        if (this._traceModel) {
            if (this._traceModel.trim().length > 0) {
                if (!this.endsWith(this._traceModel, ',')) {
                    this._traceModel += ';' + message;
                } else {
                    this._traceModel += message;
                }
            } else {
                this._traceModel = message;
            }
        } else {
            this._traceModel = message;
        }
    }

    // --------------------------------------------------
    // Getters / Setters
    // --------------------------------------------------

    get abe(): string {
        return this._abe;
    }

    set abe(newVal: string) {
        this._abe = newVal;
    }

    get debug(): boolean {
        return this._debug;
    }

    set debug(newVal: boolean) {
        console.log('Set debug to', newVal);
        this._debug = newVal;
    }

    set said(newVal: string) {
        this.controlMap['control.activity.id'] = newVal;
    }

    get scormVersion(): string {
        return this._scormVersion.toString();
    }

    set scormVersion(newVal: string) {
        if (newVal.toLowerCase() === '2004') {
            this._scormVersion = SCORM_VERSION.SCORM_2004;
            this.log('SCORM version set to SCORM 2004');
        } else if (newVal.toLowerCase() === '1.2') {
            this._scormVersion = SCORM_VERSION.SCORM_1_2;
            this.log('SCORM version set to SCORM 1.2');
        }
    }

    get terminateCallback(): any {
        return this._terminateCallback;
    }

    set terminateCallback(newVal: any) {
        this._terminateCallback = newVal;
    }
}
